import React, { useEffect, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { i18n } from "../../../translate/i18n";
import { listWhatsapps } from "../../../services/whatsapp.api";

/**
 * Selector de conexión sobre la que se simula (0383 FS-4.1).
 *
 * El canal ya está fijado por el flujo (`provider`), así que aquí solo se elige *qué conexión*
 * de ese canal. El criterio de compatibilidad es el de `assertFlowConnectionsProviderMatch`
 * (0371 WF1) y NO la igualdad de provider: un flujo WABA solo casa con conexiones WABA, y uno
 * no-oficial casa con cualquier conexión no-WABA — baileys **o** evolution.
 *
 * Importa porque de la conexión dependen los avisos del trace: los botones interactivos, por
 * ejemplo, no están soportados en Evolution.
 */
const isWaba = (p) => String(p || "").toLowerCase() === "waba";

const SimChannelSelector = ({ provider, value, onChange, disabled }) => {
  const theme = useTheme();
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await listWhatsapps();
        const list = Array.isArray(data) ? data : data?.whatsapps || [];
        if (!active) return;
        setConnections(list.filter((w) => isWaba(w.provider) === isWaba(provider)));
      } catch {
        if (active) setConnections([]);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [provider]);

  if (connections.length === 0) return null;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {i18n.t("flowbuilders.simulator.connectionTitle") || "Conexión"}
        </Typography>
        <Tooltip
          title={
            i18n.t("flowbuilders.simulator.connectionHint") ||
            "De la conexión dependen los avisos por canal: hay nodos que un proveedor no soporta."
          }
        >
          <InfoOutlined style={{ fontSize: 14, color: theme.palette.text.secondary }} />
        </Tooltip>
      </Box>

      <FormControl variant="outlined" size="small" fullWidth disabled={disabled}>
        <Select
          value={value || ""}
          displayEmpty
          onChange={(e) => onChange(e.target.value || null)}
        >
          <MenuItem value="">
            <em>
              {i18n.t("flowbuilders.simulator.connectionAuto") ||
                "Automática (primera compatible)"}
            </em>
          </MenuItem>
          {connections.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <span>{w.name}</span>
                <Chip
                  size="small"
                  label={w.provider || "baileys"}
                  style={{ height: 16, fontSize: 10 }}
                />
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SimChannelSelector;
